package com.sweetlime.dungeonamigo.model

import androidx.compose.ui.graphics.Color

object CharacterCalculator {

    fun modifier(score: Int): Int = (score - 10) / 2

    fun modifierString(score: Int): String {
        val mod = modifier(score)
        return if (mod >= 0) "+$mod" else "$mod"
    }

    fun proficiencyBonus(level: Int): Int = when {
        level <= 4  -> 2
        level <= 8  -> 3
        level <= 12 -> 4
        level <= 16 -> 5
        else        -> 6
    }

    fun spellSaveDc(spellcastingAbility: Int, profBonus: Int): Int =
        8 + modifier(spellcastingAbility) + profBonus

    fun spellAttackBonus(spellcastingAbility: Int, profBonus: Int): Int =
        modifier(spellcastingAbility) + profBonus

    fun hpColor(current: Int, max: Int): Color {
        if (max == 0) return Color(0xFFE84545)
        return when {
            current.toFloat() / max > 0.5f  -> Color(0xFFE84545)  // Ember
            current.toFloat() / max > 0.25f -> Color(0xFFE0833E)  // EmberAmber
            else                            -> Color(0xFFC03535)   // EmberDim
        }
    }

    val SKILLS = mapOf(
        "Acrobatics"     to "dexterity",
        "Animal Handling" to "wisdom",
        "Arcana"         to "intelligence",
        "Athletics"      to "strength",
        "Deception"      to "charisma",
        "History"        to "intelligence",
        "Insight"        to "wisdom",
        "Intimidation"   to "charisma",
        "Investigation"  to "intelligence",
        "Medicine"       to "wisdom",
        "Nature"         to "intelligence",
        "Perception"     to "wisdom",
        "Performance"    to "charisma",
        "Persuasion"     to "charisma",
        "Religion"       to "intelligence",
        "Sleight of Hand" to "dexterity",
        "Stealth"        to "dexterity",
        "Survival"       to "wisdom",
    )

    val SAVING_THROWS = listOf(
        "strength", "dexterity", "constitution", "intelligence", "wisdom", "charisma",
    )

    val ALIGNMENTS = listOf(
        "Lawful Good", "Neutral Good", "Chaotic Good",
        "Lawful Neutral", "True Neutral", "Chaotic Neutral",
        "Lawful Evil", "Neutral Evil", "Chaotic Evil",
    )

    val CLASS_HIT_DICE = mapOf(
        "Barbarian" to "d12",
        "Fighter"   to "d10",
        "Paladin"   to "d10",
        "Ranger"    to "d10",
        "Bard"      to "d8",
        "Cleric"    to "d8",
        "Druid"     to "d8",
        "Monk"      to "d8",
        "Rogue"     to "d8",
        "Warlock"   to "d8",
        "Sorcerer"  to "d6",
        "Wizard"    to "d6",
    )

    val SPELLCASTING_ABILITY = mapOf(
        "Bard"     to "charisma",
        "Cleric"   to "wisdom",
        "Druid"    to "wisdom",
        "Paladin"  to "charisma",
        "Ranger"   to "wisdom",
        "Sorcerer" to "charisma",
        "Warlock"  to "charisma",
        "Wizard"   to "intelligence",
    )

    // SRD full-caster spell slot progression (Bard, Cleric, Druid, Sorcerer, Wizard)
    // Index = class level - 1, array = [l1, l2, l3, l4, l5, l6, l7, l8, l9]
    private val FULL_CASTER_SLOTS = listOf(
        intArrayOf(2, 0, 0, 0, 0, 0, 0, 0, 0),  // 1
        intArrayOf(3, 2, 0, 0, 0, 0, 0, 0, 0),  // 2
        intArrayOf(4, 3, 2, 0, 0, 0, 0, 0, 0),  // 3
        intArrayOf(4, 3, 3, 1, 0, 0, 0, 0, 0),  // 4
        intArrayOf(4, 3, 3, 2, 1, 0, 0, 0, 0),  // 5
        intArrayOf(4, 3, 3, 3, 1, 0, 0, 0, 0),  // 6
        intArrayOf(4, 3, 3, 3, 2, 0, 0, 0, 0),  // 7
        intArrayOf(4, 3, 3, 3, 2, 0, 0, 0, 0),  // 8
        intArrayOf(4, 3, 3, 3, 3, 0, 0, 0, 0),  // 9
        intArrayOf(4, 3, 3, 3, 3, 0, 0, 0, 0),  // 10
        intArrayOf(4, 3, 3, 3, 3, 1, 0, 0, 0),  // 11
        intArrayOf(4, 3, 3, 3, 3, 1, 0, 0, 0),  // 12
        intArrayOf(4, 3, 3, 3, 3, 1, 1, 0, 0),  // 13
        intArrayOf(4, 3, 3, 3, 3, 1, 1, 0, 0),  // 14
        intArrayOf(4, 3, 3, 3, 3, 1, 1, 1, 0),  // 15
        intArrayOf(4, 3, 3, 3, 3, 1, 1, 1, 0),  // 16
        intArrayOf(4, 3, 3, 3, 3, 1, 1, 1, 1),  // 17
        intArrayOf(4, 3, 3, 3, 3, 1, 1, 1, 1),  // 18
        intArrayOf(4, 3, 3, 3, 3, 2, 1, 1, 1),  // 19
        intArrayOf(4, 3, 3, 3, 3, 2, 2, 1, 1),  // 20
    )

    // Half-caster spell slot progression (Paladin, Ranger)
    private val HALF_CASTER_SLOTS = listOf(
        intArrayOf(0, 0, 0, 0, 0),  // 1  — no spell slots
        intArrayOf(2, 0, 0, 0, 0),  // 2
        intArrayOf(3, 0, 0, 0, 0),  // 3
        intArrayOf(3, 0, 0, 0, 0),  // 4
        intArrayOf(4, 2, 0, 0, 0),  // 5
        intArrayOf(4, 2, 0, 0, 0),  // 6
        intArrayOf(4, 3, 0, 0, 0),  // 7
        intArrayOf(4, 3, 0, 0, 0),  // 8
        intArrayOf(4, 3, 2, 0, 0),  // 9
        intArrayOf(4, 3, 2, 0, 0),  // 10
        intArrayOf(4, 3, 3, 0, 0),  // 11
        intArrayOf(4, 3, 3, 0, 0),  // 12
        intArrayOf(4, 3, 3, 1, 0),  // 13
        intArrayOf(4, 3, 3, 1, 0),  // 14
        intArrayOf(4, 3, 3, 2, 0),  // 15
        intArrayOf(4, 3, 3, 2, 0),  // 16
        intArrayOf(4, 3, 3, 3, 1),  // 17
        intArrayOf(4, 3, 3, 3, 1),  // 18
        intArrayOf(4, 3, 3, 3, 2),  // 19
        intArrayOf(4, 3, 3, 3, 2),  // 20
    )

    fun spellSlotsForClass(characterClass: String, level: Int): Map<String, SpellSlotLevel> {
        val clampedLevel = level.coerceIn(1, 20)
        val idx = clampedLevel - 1

        val slots: IntArray? = when (characterClass) {
            "Bard", "Cleric", "Druid", "Sorcerer", "Wizard" -> FULL_CASTER_SLOTS[idx]
            "Paladin", "Ranger"                             -> HALF_CASTER_SLOTS[idx]
            "Warlock"                                       -> null  // handled separately
            else                                            -> null  // non-spellcaster
        }

        if (characterClass == "Warlock") {
            return buildWarlockSlots(clampedLevel)
        }

        if (slots == null) return emptyMap()
        return buildMap {
            slots.forEachIndexed { i: Int, count: Int ->
                if (count > 0) put("${i + 1}", SpellSlotLevel(total = count, used = 0))
            }
        }
    }

    private fun buildWarlockSlots(level: Int): Map<String, SpellSlotLevel> {
        val (slotLevel, slotCount) = when {
            level >= 17 -> 5 to 4
            level >= 11 -> 5 to 3
            level >= 9  -> 5 to 2
            level >= 7  -> 4 to 2
            level >= 5  -> 3 to 2
            level >= 3  -> 2 to 2
            level == 2  -> 1 to 2
            else        -> 1 to 1
        }
        return mapOf("$slotLevel" to SpellSlotLevel(total = slotCount, used = 0))
    }

    fun raceSpeed(race: String): Int = when (race.lowercase().trim()) {
        "dwarf", "halfling", "gnome" -> 25
        else                         -> 30
    }

    fun abilityScore(character: Character, ability: String): Int = when (ability) {
        "strength"     -> character.strength
        "dexterity"    -> character.dexterity
        "constitution" -> character.constitution
        "intelligence" -> character.intelligence
        "wisdom"       -> character.wisdom
        "charisma"     -> character.charisma
        else           -> 10
    }

    fun skillBonus(character: Character, skill: String): Int {
        val ability = SKILLS[skill] ?: return 0
        val abilityMod = modifier(abilityScore(character, ability))
        val prof = proficiencyBonus(character.level)
        return when {
            skill in character.expertiseSkills    -> abilityMod + prof * 2
            skill in character.skillProficiencies -> abilityMod + prof
            else                                  -> abilityMod
        }
    }

    fun savingThrowBonus(character: Character, ability: String): Int {
        val abilityMod = modifier(abilityScore(character, ability))
        val prof = proficiencyBonus(character.level)
        return if (ability in character.savingThrowProficiencies) abilityMod + prof else abilityMod
    }
}